import React, { useState, useRef } from 'react';
import { View, Text, StyleSheet, Linking, Dimensions, TouchableOpacity } from 'react-native';
import { parse } from 'search-params';
import i18n from 'i18n-js';
import { moderateScale, isIOS, navigateTo, isPhone, Alert, isWeb } from '@helpers';
import { connect } from 'react-redux';

import TabletWrapper from '@components/auntification/tablerWrapper/index';
import {
  AUTH_LOGO_MARGIN_TOP_CONNECTOR,
  AUTH_LOGO_OFFSET_CONNECTOR,
  AUTH_OFFSET_CONNECTOR,
} from '../tablerWrapper/constants';
import Logo from '@components/logo/index';
import { SmallLoading } from '@components/loading';
import ColorsWrapper from '@styles/colors';
import Svg from '@styles/icons/svg';
import { font } from '@styles/fonts';
import { notchTop } from '@components/frame';

import { validateOnboarding } from '@reducers/onBoarding';

const DownloadConnector = ({ C, validateOnboarding, route, Scanner }) => {
  const devWidth = Dimensions.get('window').width;

  const [loading, setLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const scanned = useRef(false);

  const returnHome = () => {
    navigateTo('Login');
  };

  const submit = async (c) => {
    setLoading(true);
    const code = c || globalCode;
    const result = await validateOnboarding(code, true);

    if (result?.onboardingStatus === 'UnregisteredConnectorConnectionEstablished') {
      navigateTo('CreatePassword', { data: route.params, scenario: 'onBoarding' });
      return;
    } else {
      Alert({ title: 'Wrong code' });
    }
    setLoading(false);
  };

  const invalidQr = () => {
    setLoading(false);

    scanned.current = false;
    Alert({ title: i18n.t('Error'), text: i18n.t('Wrong QR code') });
  };

  const handleBarCodeScanned = async (code) => {
    try {
      const data = code.value;

      scanned.current = true;
      setLoading(true);

      if (!data) {
        return invalidQr();
      }

      const isQRLink = data.indexOf('/qr?') > 0;
      if (isQRLink) {
        const { sessionKey } = parse(data);
        if (sessionKey) {
          return await submit(sessionKey);
        }

        return invalidQr();
      }
      return invalidQr();
    } catch (e) {
      console.log(e, 'e');
      invalidQr();
    }
  };

  if (loading) {
    return (
      <View style={{ width: '100%', flex: 1, paddingTop: notchTop, backgroundColor: C('Background/Light/Detail') }}>
        <View style={{ width: '100%', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <SmallLoading />
        </View>
      </View>
    );
  }

  if (showScanner) {
    return <Scanner {...{ C, scanned, handleBarCodeScanned, setShowScanner }} />;
  }

  const openLink = () => {
    const url = 'https://smarfac.com/download';
    Linking.canOpenURL(url).then((supported) => {
      if (supported) {
        Linking.openURL(url);
      } else {
        Alert({
          title: url,
          actions: [
            {
              text: 'Ok',
              onPress: () => console.log('Ok Pressed'),
              style: 'primary',
            },
          ],
        });
      }
    });
  };

  return (
    <TabletWrapper offset={AUTH_OFFSET_CONNECTOR}>
      <View style={styles.topContainer}>
        <View style={styles.logoBox}>
          <Logo
            title={i18n.t('Check your email')}
            small
            marginTop={AUTH_LOGO_MARGIN_TOP_CONNECTOR}
            offset={AUTH_LOGO_OFFSET_CONNECTOR}
          />
        </View>
        <Text style={[{ color: C('Label/Light/Primary'), textAlign: 'center' }, font['Body']]}>
          {i18n.t('You will now receive an email with the download link for the free Connector.')}
        </Text>
        <TouchableOpacity activeOpacity={1} onPress={openLink}>
          <Text style={[{ color: C('Label/Light/Primary'), marginTop: moderateScale(20), textAlign: 'center' }, font['Body']]}>
            {i18n.t('You can also download from')}
            <Text style={{ color: C('Link/Light/Primary') }}> {i18n.t('smarfac.com/download')}</Text>
          </Text>
        </TouchableOpacity>
        <Text style={[{ color: C('Label/Light/Primary'), marginTop: moderateScale(20), textAlign: 'center' }, font['Body']]}>
          {i18n.t('Install the Connector on a Windows Server or Computer that is always on.')}
        </Text>
        {!isWeb && (
          <TouchableOpacity onPress={() => setShowScanner(true)}>
            <View style={{ flexDirection: 'row', marginTop: moderateScale(40) }}>
              <Svg icon="qr" width={moderateScale(20)} height={moderateScale(20)} color={'Button/Light/Login'} />
              <Text style={[font['Headline'], { color: C('Button/Light/Login'), marginLeft: moderateScale(10) }]}>
                {i18n.t('Scan QR Code on Connector')}
              </Text>
            </View>
          </TouchableOpacity>
        )}
        <Text style={[font['Title 2'], { color: C('Label/Light/Secondary'), marginTop: moderateScale(40), textAlign: 'center', fontWeight: '300' }]}>
          {i18n.t('Waiting for Connector...')}
        </Text>
        <View style={{ width: '100%', justifyContent: 'center', marginTop: moderateScale(40) }}>
          <SmallLoading />
        </View>
      </View>
      <View style={styles.bottomContainer}>
        <TouchableOpacity onPress={returnHome}>
          <Text style={[font['Button 2'], { color: C('Label / Light / Secondary'), textAlign: 'center' }]}>{i18n.t('Back to start screen')}</Text>
        </TouchableOpacity>
      </View>
    </TabletWrapper>
  );
};

export default connect(null, { validateOnboarding })(ColorsWrapper(DownloadConnector));

const styles = StyleSheet.create({
  actionText: {
    fontWeight: 'bold',
    fontSize: moderateScale(12),
    textAlign: 'center',
    letterSpacing: moderateScale(0.01),
  },
  childImgBox: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    marginBottom: moderateScale(!isPhone ? 33 : 17),
    marginTop: moderateScale(-5),
  },
  welcomeText: {
    fontWeight: 'normal',
    fontSize: moderateScale(14),
    textAlign: 'center',
    letterSpacing: moderateScale(0.02),
    lineHeight: moderateScale(21),
  },
  logoBox: {
    alignItems: 'center',
    marginBottom: moderateScale(!isPhone ? 25 : 0),
  },
  topContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: moderateScale(!isPhone ? 382 : 302),
  },
  bottomContainer: {
    marginTop: moderateScale(40),
    maxWidth: moderateScale(!isPhone ? 382 : 302),
    marginBottom: moderateScale(20),
  },
  buttonBox: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    height: '100%',
  },
});
